import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState, type MouseEvent } from "react";

type ViewerKind = "terms" | "privacy";

/** Map in-document legal URLs (any host) to the modal viewer kind. */
function hrefToViewerKind(absoluteHref: string): ViewerKind | null {
  try {
    const url = new URL(absoluteHref);
    const path = url.pathname.replace(/\/$/u, "") || "/";
    if (path === "/privacy" || path === "/privacy-policy") {
      return "privacy";
    }
    if (path === "/terms") {
      return "terms";
    }
    return null;
  } catch {
    return null;
  }
}

export function LegalDocumentLinksWithModal() {
  const [viewer, setViewer] = useState<ViewerKind | null>(null);
  const [documentHtml, setDocumentHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (viewer === null) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setDocumentHtml(null);
    void (async () => {
      try {
        const response = await fetch(`/api/legal/document-html?kind=${viewer}`);
        const data = (await response.json()) as { html?: string; error?: string };
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          setLoadError(data.error ?? "Could not load document");
          return;
        }
        setDocumentHtml(data.html ?? "");
      } catch {
        if (!cancelled) {
          setLoadError("Could not load document");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewer]);

  const handleLegalHtmlClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest("a[href]");
    if (!anchor) {
      return;
    }
    const kind = hrefToViewerKind((anchor as HTMLAnchorElement).href);
    if (kind === null) {
      return;
    }
    event.preventDefault();
    setViewer(kind);
  }, []);

  return (
    <>
      <Typography variant="body2" component="div">
        <Link component="button" type="button" onClick={() => setViewer("terms")}>
          Terms of Service
        </Link>
        {" · "}
        <Link component="button" type="button" onClick={() => setViewer("privacy")}>
          Privacy Policy
        </Link>
      </Typography>

      <Dialog
        open={viewer !== null}
        onClose={() => setViewer(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {viewer === "privacy" ? "Privacy Policy" : "Terms of Service"}
          <IconButton
            aria-label="Close document"
            onClick={() => setViewer(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {loadError !== null && !loading && (
            <Typography color="error" variant="body2">
              {loadError}
            </Typography>
          )}
          {documentHtml !== null && !loading && loadError === null && (
            <div onClick={handleLegalHtmlClick} dangerouslySetInnerHTML={{ __html: documentHtml }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
